import React from 'react';
import { BreadcrumbSchema, FAQSchema, ProductSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, RichFAQ, RelatedLinks, RichCTA, ChargerCard,
} from '../RichBlocks';
import { EVChargingCalculator } from '../RichCalculators';
import { ServiceAgreementBlock } from '@/components/seo/content/ContentBlocks';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

export default function ZaptecGo2Nordjylland({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });
  const charger = ENKEL_PRODUCTS.chargers.zaptec_go_2;

  const faqs = [
    { question: 'Hvad koster en Zaptec Go 2 ladeboks i Nordjylland?', answer: 'Zaptec Go 2 koster 5.999 kr. hos enkel energi. Prisen er den samme i hele Nordjylland og resten af landet. Saving Mode, serviceaftale og laderabat er inkluderet til 0 kr./md. Installation kommer oveni fra 1.300 kr.' },
    { question: 'Er Saving Mode inkluderet med Zaptec Go 2?', answer: 'Ja. Når du har en el-aftale hos enkel energi, er Saving Mode inkluderet til 0 kr./md. Det samme gælder serviceaftale og laderabat på 10 øre/kWh. Saving Mode planlægger automatisk opladningen til de billigste timer.' },
    { question: 'Hvad koster installation af Zaptec Go 2 i Nordjylland?', answer: 'Installation starter fra 1.300 kr. ved eksisterende stik og fra 3.000 kr. ved nyt stik. Arbejdet udføres af en autoriseret elinstallatør og tager typisk 1–2 timer. I Nordjylland leverer netselskaber som N1 strømmen.' },
    { question: 'Passer Zaptec Go 2 til min elbil?', answer: 'Ja. Zaptec Go 2 har et Type 2-stik, som er standarden for alle moderne elbiler i Europa – Tesla, VW, Kia, Hyundai, Polestar, Audi m.fl. Boksen leverer op til 22 kW.' },
    { question: 'Er Zaptec Go 2 V2G-klar?', answer: 'Ja. Zaptec Go 2 er forberedt til V2G (Vehicle-to-Grid), så bilen på sigt kan sende strøm tilbage til nettet, når lovgivningen og elselskabsaftaler er på plads i Danmark (ventes 2026–2027). Du fremtidssikrer dig uden ekstra omkostning.' },
    { question: 'Hvorfor vælge enkel energi i Nordjylland?', answer: 'Nordjylland ligger i priszone DK1, hvor vindkraft ofte presser spotprisen ned. Med spotpris + 0 øre tillæg fra 18 kr./md. får du fuldt udbytte af de lave priser. NicolasKJ.dk har kåret os billigst og ELBIIL.dk som bedste ladeløsning.' },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'specs', label: 'Specifikationer' },
    { id: 'inkluderet', label: 'Inkluderet' },
    { id: 'installation', label: 'Installation' },
    { id: 'beregner', label: 'Beregn besparelse' },
    { id: 'nordjylland', label: 'El i Nordjylland' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Product">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Ladebokse', path: '/opladere' }, { name: 'Zaptec Go 2 i Nordjylland', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ProductSchema name="Zaptec Go 2" description="V2G-ready ladeboks 22 kW inkl. Saving Mode og serviceaftale" image={charger.image} price={charger.price} sku="zaptec_go_2" brand="Zaptec" />

      <RichHero
        eyebrow="Ladeboks · Nordjylland (DK1) · 2026"
        h1="Zaptec Go 2 ladeboks i Nordjylland 2026"
        intro="Alt om Zaptec Go 2 til dig i Nordjylland: pris, specifikationer, V2G, installation og hvad du sparer med Saving Mode og laderabat. Spotpris uden tillæg i hele DK1."
        badges={['22 kW', 'V2G-ready', 'Saving Mode inkl.', '5 års garanti']}
        updated={updated}
        image="https://images.unsplash.com/photo-1617704548623-340376564e68?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '5.999 kr.', label: 'pris inkl. Saving Mode + service' },
          { value: '22 kW', label: 'ladekraft' },
          { value: '0 kr./md.', label: 'serviceaftale' },
          { value: 'DK1', label: 'Nordjylland (Vestdanmark)' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Zaptec Go 2 i Nordjylland – kort fortalt">
        <AnswerBox question="Hvad koster Zaptec Go 2 i Nordjylland, og hvad er inkluderet?">
          Zaptec Go 2 koster <strong>5.999 kr.</strong> hos enkel energi – samme pris i hele Nordjylland. Du får en
          <strong> 22 kW V2G-ready ladeboks</strong> med Saving Mode, serviceaftale og 10 øre/kWh laderabat inkluderet til <strong>0 kr./md.</strong>
          Installation kommer oveni fra 1.300 kr. Nordjylland ligger i priszone DK1, hvor du med spotpris + 0 øre tillæg får fuldt udbytte af de lave vindpriser.
        </AnswerBox>
        <div className="grid sm:grid-cols-2 gap-6 my-6 items-start">
          <ChargerCard charger={charger} note="V2G-ready · Saving Mode inkl." />
          <div className="space-y-3 text-gray-700">
            <p>
              Zaptec Go 2 er det nyeste skud på stammen fra den norske producent Zaptec. Den er bygget til fremtidens elnet med
              indbygget skærm, Wi-Fi + 4G og forberedelse til V2G. Den er et oplagt valg for nordjyske elbilejere, der vil have en
              fremtidssikret løsning og udnytte de svingende spotpriser i DK1.
            </p>
            <p>
              I Nordjylland leverer lokale netselskaber som N1 strømmen, mens ladeboksen automatisk placerer opladningen i de billigste timer
              via Saving Mode.
            </p>
          </div>
        </div>
      </ProseSection>

      <ProseSection id="specs" heading="Zaptec Go 2 specifikationer" alt>
        <div className="my-2">
          <ComparisonTable
            caption="Tekniske specifikationer for Zaptec Go 2"
            columns={[{ name: 'Zaptec Go 2', highlight: true }]}
            rows={[
              { label: 'Ladekraft', values: ['Op til 22 kW (3-faset)'] },
              { label: 'Stiktype', values: ['Type 2 – passer alle elbiler'] },
              { label: 'Forbindelse', values: ['Wi-Fi + 4G'] },
              { label: 'V2G', values: ['Ready (tovejs-forberedt)'] },
              { label: 'Skærm', values: ['Indbygget'] },
              { label: 'Load balancing', values: [true] },
              { label: 'Garanti', values: ['5 år'] },
              { label: 'Saving Mode', values: [true] },
            ]}
          />
        </div>
      </ProseSection>

      <ProseSection id="inkluderet" heading="Alt det her er inkluderet i prisen">
        <p>Hos enkel energi handler du ikke bare en kasse til væggen – du får en komplet ladeløsning. Når du har en el-aftale, er følgende inkluderet til 0 kr./md.:</p>
        <div className="my-6"><ServiceAgreementBlock variant="full" /></div>
      </ProseSection>

      <ProseSection id="installation" heading="Installation i Nordjylland" alt>
        <FactGrid columns={3} facts={[
          { value: 'Fra 1.300 kr.', label: 'Eksisterende stik', sub: 'Hurtig montering' },
          { value: 'Fra 3.000 kr.', label: 'Nyt stik / kabelføring', sub: 'Standard installation' },
          { value: '1–2 timer', label: 'Typisk arbejdstid', sub: 'Autoriseret elinstallatør' },
        ]} />
        <p className="mt-6">
          Al installation udføres af en autoriseret elinstallatør, så din opladning fungerer sikkert og lovligt. Bor du i hus med egen indkørsel
          i fx Aalborg, Frederikshavn, Hjørring eller Thisted, er en standardinstallation oftest ligetil.
        </p>
      </ProseSection>

      <ProseSection id="beregner" heading="Hvad sparer du med Zaptec Go 2 og enkel?">
        <p>Kombinationen af spotpris uden tillæg, Saving Mode og 10 øre/kWh laderabat gør opladningen markant billigere. Prøv beregneren:</p>
        <div className="my-6"><EVChargingCalculator /></div>
        <AnswerBox>
          Laderabatten (ca. 792 kr./år ved 7.920 kWh) er større end abonnementet på enkel kvartal (216 kr./år). For mange elbilejere
          betyder det reelt, at <strong>el-aftalen bliver gratis</strong> – og det overskydende beløb modregnes resten af din elregning.
        </AnswerBox>
      </ProseSection>

      <ProseSection id="nordjylland" heading="Hvorfor enkel energi i Nordjylland?" alt>
        <p>
          Nordjylland ligger i priszone DK1 (Vestdanmark), der er tæt forbundet med den store vindkraftproduktion i Jylland. Det presser ofte
          spotprisen ned, særligt i blæsende perioder. For at få fuldt udbytte af de lave priser skal du dog vælge et selskab uden tillæg –
          ellers spises gevinsten op. Hos enkel betaler du spotpris + 0 øre fra 18 kr./md., uden binding og uden depositum.
        </p>
        <p>
          Vores løsning er anerkendt udefra: NicolasKJ.dk har kåret os billigst, ELBIIL.dk gav os titlen som bedste ladeløsning, og tre af
          vores produkter lå i top 10 i en stor uafhængig forbrugertest i 2026.
        </p>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/zaptec-go-2', label: 'Zaptec Go 2 – produktside' },
        { path: '/sammenlign/zaptec-go-vs-go-2', label: 'Zaptec Go vs Go 2' },
        { path: '/sammenlign/ladeboks-v2g-ready', label: 'Ladebokse med V2G' },
        { path: '/sammenlign/ladeboks-installation', label: 'Installation af ladeboks' },
        { path: '/opladere', label: 'Alle ladebokse' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA headline="Bestil Zaptec Go 2 i Nordjylland" subtext="V2G-ready, 22 kW og Saving Mode inkluderet. Spotpris uden tillæg i DK1." primary={{ path: '/zaptec-go-2', text: 'Se Zaptec Go 2' }} secondary={{ path: '/opladere', text: 'Alle ladebokse' }} />
      <SEODisclaimer page={page} />
    </article>
  );
}